import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  author: string;
  published_at: string;
  image_url: string;
  thumbnail_url: string | null;
}

const Blog = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .lte('published_at', new Date().toISOString())
        .order('published_at', { ascending: false });
      
      if (!error && data) {
        setPosts(data);
      }
      setLoading(false);
    };

    fetchPosts();
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Blog du Collectif Ibbü",
    "url": "https://collectif-ibbu.org/blog",
    "description": "Actualités et réflexions des conseillers iAdvize/Ibbu",
    "publisher": {
      "@type": "Organization",
      "name": "Collectif Ibbü"
    }
  };

  return (
    <>
      <SEO
        title="Blog Collectif Ibbü | Actualités conseillers iAdvize Ibbu"
        description="Actualités, témoignages et réflexions des anciens conseillers iAdvize/Ibbu. Suivez l'évolution de notre action collective."
        canonical="https://collectif-ibbu.org/blog"
        structuredData={structuredData}
      />
      <div className="min-h-screen flex flex-col">
        <main className="flex-1">
        <div className="bg-gradient-to-b from-primary/10 to-background py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <Breadcrumbs items={[{ label: "Blog" }]} />
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Blog</h1>
            <p className="text-xl text-muted-foreground">
              Actualités et réflexions du Collectif Ibbü
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-12">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Chargement des articles...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Aucun article pour le moment.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, index) => (
                <Card key={post.id} className="flex flex-col">
                  {(post.thumbnail_url || post.image_url) && (
                    <div className="h-48 overflow-hidden rounded-t-lg">
                      <img 
                        src={post.thumbnail_url || post.image_url} 
                        alt={post.title}
                        width={480}
                        height={252}
                        className="w-full h-full object-cover"
                        {...(index === 0
                          ? { fetchPriority: "high" as const, decoding: "async" as const }
                          : { loading: "lazy" as const, decoding: "async" as const })}
                      />
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="line-clamp-2">{post.title}</CardTitle>
                    <CardDescription>
                      {new Date(post.published_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })} • Par {post.author}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1">
                    <p className="text-muted-foreground line-clamp-3">{post.excerpt}</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full group">
                      <Link to={`/blog/${post.slug}`}>
                        Lire la suite
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
      </div>
    </>
  );
};

export default Blog;